/**
 * WordPress dependencies
 */
import { select as doSelect, useSelect } from '@safe-wordpress/data';
import type { SelectFunction as Select } from '@safe-wordpress/data';

/**
 * External dependencies
 */
import { castArray, find, isFunction } from 'lodash';
import type { EntityKind, ExperimentType as ET, Maybe } from '@nab/types';

/**
 * Internal dependencies
 */
import { isEmpty } from './validators';

type ExperimentType = ET & {
	readonly _isDisabled: boolean;
	readonly _usesPresetAlternatives: boolean;
	readonly _hasPresetAlternatives: boolean;
};

export function useIsExperimentTypeDisabled(
	type: Maybe< ET >,
	postTypes: ReadonlyArray< EntityKind >
): boolean {
	return useSelect(
		( select ) => !! type && isDisabled( type, postTypes, select ),
		[ type, postTypes ]
	);
}

export function isExperimentTypeDisabled(
	type: ET,
	postTypes: ReadonlyArray< EntityKind >
): boolean {
	return isDisabled( type, postTypes, doSelect );
}

// =======
// HELPERS
// =======

function isDisabled(
	type: ET,
	postTypes: ReadonlyArray< EntityKind >,
	select: Select
): boolean {
	if ( ! type ) {
		return true;
	}

	const extentedType: ExperimentType = {
		...type,
		_usesPresetAlternatives: isFunction( type.supports.presetAlternatives ),
		_hasPresetAlternatives: !! type.supports.presetAlternatives?.(
			select,
			'nab-check-availability'
		),
		_isDisabled:
			isFunction( type.checks.isTestTypeEnabled ) &&
			! type.checks.isTestTypeEnabled( select ),
	};

	if ( extentedType._isDisabled ) {
		return true;
	}

	if (
		isLimitedToCertainPostTypes( extentedType ) &&
		! areSupportedPostTypesAvailable( extentedType, postTypes, select )
	) {
		return true;
	}

	return (
		extentedType._usesPresetAlternatives &&
		! extentedType._hasPresetAlternatives
	);
}

function isLimitedToCertainPostTypes( experimentType: ExperimentType ) {
	return (
		! isEmpty( experimentType.supports.postTypes ) ||
		! isEmpty( experimentType.supports.postTypeExceptions )
	);
}

function areSupportedPostTypesAvailable(
	experimentType: ExperimentType,
	postTypes: ReadonlyArray< EntityKind >,
	select: Select
) {
	if ( experimentType.supports.postTypes ) {
		const postTypeNames = castArray( experimentType.supports.postTypes );
		return !! find( postTypes, ( postType ) =>
			postTypeNames.includes( postType.name )
		);
	}

	if ( experimentType.supports.postTypeExceptions ) {
		const postTypeExceptions = isFunction(
			experimentType.supports.postTypeExceptions
		)
			? experimentType.supports.postTypeExceptions( select )
			: castArray( experimentType.supports.postTypeExceptions );
		return !! find(
			postTypes,
			( postType ) => ! postTypeExceptions.includes( postType.name )
		);
	}

	return false;
}
